/** @jsxImportSource react */
import { useRef, useState } from 'react'
import { Box, Button, IconButton, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { filesAPI } from '../../api/crm'
import { useFeatures, useTenantKey } from '../../hooks/useCrmQueries'
import { downloadBlob, formatDate, formatRelativeTime } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../common/NotificationProvider'
import ConfirmDialog from '../common/ConfirmDialog'
import { useNameOf } from './PersonInput'

/** Types the server serves inline (they can't run script); everything else is a download. */
const PREVIEWABLE = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp']

export const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const iconFor = (type) => (type === 'application/pdf' ? PictureAsPdfOutlinedIcon : type.startsWith('image/') ? ImageOutlinedIcon : InsertDriveFileOutlinedIcon)

/** Files on a record: anyone who can view it downloads; editors upload (button or drop); uploader/admin remove. */
const RecordFiles = ({ record, canUpload }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const nameOf = useNameOf()
  const { maxFileSizeMb } = useFeatures()
  const input = useRef(null)
  const [progress, setProgress] = useState(null)
  const [dragging, setDragging] = useState(false)
  const [removing, setRemoving] = useState(null)
  const queryKey = [tenantId, 'records', 'files', record.id]
  const { data: files = [], isLoading, error } = useQuery({ queryKey, queryFn: () => filesAPI.list(record.id) })
  // the timeline shows uploads and removals: both live under [tenant, 'records']
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey })
    queryClient.invalidateQueries({ queryKey: [tenantId, 'records', 'activity', record.id] })
  }

  const upload = useMutation({
    mutationFn: async (list) => {
      for (const file of list) {
        if (file.size > maxFileSizeMb * 1024 * 1024) throw new Error(`${file.name} is larger than ${maxFileSizeMb} MB`)
      }
      for (const [i, file] of list.entries()) {
        setProgress({ name: file.name, index: i + 1, count: list.length, percent: 0 })
        await filesAPI.upload(record.id, file, (e) => e.total && setProgress((p) => ({ ...p, percent: Math.round((e.loaded / e.total) * 100) })))
      }
      return list.length
    },
    onSuccess: (count) => notify(count === 1 ? 'File uploaded' : `${count} files uploaded`),
    onError: (e) => notify(getErrorMessage(e), 'error'),
    onSettled: () => { setProgress(null); refresh() },
  })
  const remove = useMutation({
    mutationFn: (file) => filesAPI.remove(record.id, file.id),
    onSuccess: () => { setRemoving(null); notify('File removed'); refresh() },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })

  const choose = (list) => { if (list?.length) upload.mutate([...list]) }
  const download = async (file) => {
    try {
      downloadBlob(await filesAPI.content(record.id, file.id), file.fileName)
    } catch (e) {
      notify(getErrorMessage(e), 'error')
    }
  }
  const preview = async (file) => {
    const tab = window.open('', '_blank') // opened now, while the click still counts, so it isn't blocked
    try {
      const blob = await filesAPI.content(record.id, file.id, true)
      const url = URL.createObjectURL(new Blob([blob], { type: file.contentType }))
      if (tab) tab.location.href = url
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      tab?.close()
      notify(getErrorMessage(e), 'error')
    }
  }

  const dropProps = canUpload ? {
    onDragOver: (e) => { e.preventDefault(); setDragging(true) },
    onDragLeave: () => setDragging(false),
    onDrop: (e) => { e.preventDefault(); setDragging(false); choose(e.dataTransfer.files) },
  } : {}

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mt: 2, borderStyle: dragging ? 'dashed' : 'solid', borderColor: dragging ? 'primary.main' : 'divider' }} {...dropProps}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>Files{files.length > 0 && ` · ${files.length}`}</Typography>
        {canUpload && (
          <>
            <input ref={input} type="file" multiple hidden onChange={(e) => { choose(e.target.files); e.target.value = '' }} />
            <Button size="small" startIcon={<UploadFileOutlinedIcon />} onClick={() => input.current?.click()} disabled={upload.isPending}>Upload</Button>
          </>
        )}
      </Box>
      {progress && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">Uploading {progress.name}{progress.count > 1 ? ` (${progress.index} of ${progress.count})` : ''}…</Typography>
          <LinearProgress variant="determinate" value={progress.percent} />
        </Box>
      )}
      {isLoading && <Typography variant="body2" color="text.secondary">Loading files…</Typography>}
      {error && <Typography variant="body2" color="error">{getErrorMessage(error)}</Typography>}
      {!isLoading && !error && files.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {canUpload ? `No files yet. Drop files here or use Upload (up to ${maxFileSizeMb} MB each).` : 'No files.'}
        </Typography>
      )}
      <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
        {files.map((f) => {
          const Icon = iconFor(f.contentType)
          return (
            <Box key={f.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
              <Icon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap title={f.fileName}>{f.fileName}</Typography>
                <Tooltip title={formatDate(f.uploadedOn)}>
                  <Typography variant="caption" color="text.secondary">{formatSize(f.sizeBytes)} · {nameOf(f.uploadedBy)} · {formatRelativeTime(f.uploadedOn)}</Typography>
                </Tooltip>
              </Box>
              {PREVIEWABLE.includes(f.contentType) && (
                <Tooltip title="Open"><IconButton size="small" onClick={() => preview(f)} aria-label={`Open ${f.fileName}`}><VisibilityOutlinedIcon fontSize="small" /></IconButton></Tooltip>
              )}
              <Tooltip title="Download"><IconButton size="small" onClick={() => download(f)} aria-label={`Download ${f.fileName}`}><DownloadOutlinedIcon fontSize="small" /></IconButton></Tooltip>
              {f.canDelete && (
                <Tooltip title="Remove"><IconButton size="small" onClick={() => setRemoving(f)} aria-label={`Remove ${f.fileName}`}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
              )}
            </Box>
          )
        })}
      </Stack>
      <ConfirmDialog open={!!removing} title="Remove file?" message={`"${removing?.fileName}" will be deleted for everyone. This can't be undone.`}
        confirmLabel="Remove" destructive busy={remove.isPending} onConfirm={() => remove.mutate(removing)} onClose={() => setRemoving(null)} />
    </Paper>
  )
}

export default RecordFiles
